import React, { useState, useEffect } from 'react';
import { Horse, HorseWithCost, OwnerCost, DewormingParams } from './types';
import {
  packagesToBuy,
  newWeight,
  costPerAnimal,
  costPerOwner,
  sumOfCost
} from './dewormingCalculations';
import { generateBillingPDF, generateMeasurementPDF } from './pdfGenerator';
import './DewormingCalculator.css';

// Import horse data loader
import { loadHorsesData } from './horsesData';

const DewormingCalculator: React.FC = () => {
  // State for input parameters
  const [params, setParams] = useState<DewormingParams>({
    wurmkur: 'Equimax',
    tagDerGabe: new Date(),
    kgProPack: 700,
    preisProPack: 19.95,
    lohnProTier: 1.50,
    planning: false
  });

  // State for horses
  const [horses, setHorses] = useState<Horse[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // State for calculated results
  const [horsesWithCost, setHorsesWithCost] = useState<HorseWithCost[]>([]);
  const [ownersCost, setOwnersCost] = useState<OwnerCost[]>([]);
  const [totalIncome, setTotalIncome] = useState<string>('0,00 €');
  const [packagesToOrder, setPackagesToOrder] = useState<number>(0);
  const [showDetails, setShowDetails] = useState<boolean>(false);

  // Determine if a horse should be visible based on hidden status and contract end date
  const isHorseVisible = (horse: Horse): boolean => {
    // If not hidden, always visible
    if (!horse.hidden) return true;

    // If hidden, check if contract end date is in current month or future
    if (horse.contract?.end_date) {
      const endDate = new Date(horse.contract.end_date);
      const today = new Date();

      // Get year-month for comparison (year * 12 + month gives comparable values)
      const endYearMonth = endDate.getFullYear() * 12 + endDate.getMonth();
      const currentYearMonth = today.getFullYear() * 12 + today.getMonth();

      // Visible if contract ends in current month or later
      return endYearMonth >= currentYearMonth;
    }

    // If hidden and no end date, hide it
    return false;
  };

  // Load horses on component mount
  useEffect(() => {
    loadHorsesData()
      .then(({ horses }) => {
        const visibleHorses = horses.filter(horse => isHorseVisible(horse));
        setHorses(visibleHorses);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error('Failed to load horses:', error);
        alert('Fehler beim Laden der Pferde-Daten');
        setIsLoading(false);
      });
  }, []);

  // Calculate whenever horses or params change
  useEffect(() => {
    if (horses.length > 0 && params.kgProPack > 0) {
      calculate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [horses, params]);

  const calculate = () => {
    // Calculate packages to buy
    const packages = packagesToBuy(horses, params.kgProPack);
    setPackagesToOrder(packages);

    // Calculate new weights and costs
    const horsesWithNewWeight = newWeight(horses, params.kgProPack);
    const horsesWithCostData = costPerAnimal(
      horsesWithNewWeight,
      params.kgProPack,
      params.preisProPack,
      params.lohnProTier
    );
    setHorsesWithCost(horsesWithCostData);

    // Calculate cost per owner
    const ownerCostData = costPerOwner(horsesWithCostData);
    setOwnersCost(ownerCostData);

    // Calculate total income
    const total = sumOfCost(horsesWithCostData);
    setTotalIncome(total);
  };

  const handleInputChange = (field: keyof DewormingParams, value: any) => {
    setParams(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const formatDate = (date: Date): string => {
    const day = String(date.getDate()).padStart(2, '0');
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const year = date.getFullYear();
    return `${year}-${month}-${day}`;
  };

  const handlePrintBilling = () => {
    generateBillingPDF(ownersCost, horsesWithCost, params);
  };

  const handlePrintMeasurement = () => {
    generateMeasurementPDF(horsesWithCost);
  };

  if (isLoading) {
    return (
      <div className="deworming-calculator">
        <div style={{ padding: '2rem', textAlign: 'center' }}>
          <p>Lade Pferde-Daten...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="deworming-calculator">
      <header className="header">
        <h2>Wurmkur berechnen</h2>
        <h4>
          {horses.length} Tiere registriert. Preise werden auf die nächstliegenden 10 Cent Beträge gerundet.
          Nicht verbrauchte Reste werden auf alle Tiere im Verhältnis der Gewichte umgelegt.
        </h4>
      </header>

      <div className="info-box">
        <strong>Wichtiger Hinweis zur Gewichtsmessung:</strong>
        <p>
          Die Wurmkur kann nur in Schritten zu 50 kg Pferdegewicht gegeben werden.
          Die Messung erfolgt daher in ausreichender Genauigkeit mit dem Equimax Maßband.
          Das abgelesene Gewicht wird auf die nächsten 50 kg aufgerundet.
          Die Gewichtsangaben in der Abrechnung können daher bis zu maximal 50 kg vom tatsächlichen Gewicht abweichen!
        </p>
      </div>

      <article className="content">
        <form className="calculation-form">
          <table>
            <tbody>
              <tr>
                <td>Wurmkur Produkt</td>
                <td>
                  <input
                    type="text"
                    value={params.wurmkur}
                    onChange={(e) => handleInputChange('wurmkur', e.target.value)}
                  />
                </td>
              </tr>
              <tr>
                <td>Datum der Gabe</td>
                <td>
                  <input
                    type="date"
                    value={formatDate(params.tagDerGabe)}
                    onChange={(e) => handleInputChange('tagDerGabe', new Date(e.target.value))}
                  />
                </td>
                <td>
                  <label>
                    <input
                      type="checkbox"
                      checked={params.planning}
                      onChange={(e) => handleInputChange('planning', e.target.checked)}
                    />
                    geplant
                  </label>
                </td>
              </tr>
              <tr>
                <td>1 Packung reicht für</td>
                <td>
                  <input
                    type="number"
                    min="0"
                    max="1000"
                    step="50"
                    value={params.kgProPack}
                    onChange={(e) => handleInputChange('kgProPack', parseFloat(e.target.value))}
                  />
                </td>
                <td>kg</td>
              </tr>
              <tr>
                <td>Preis pro Packung netto</td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    value={params.preisProPack}
                    onChange={(e) => handleInputChange('preisProPack', parseFloat(e.target.value))}
                  />
                </td>
                <td>€</td>
              </tr>
              <tr>
                <td>
                  Lohn pro Tier netto<br />für die Gabe der Kur
                </td>
                <td>
                  <input
                    type="number"
                    step="0.01"
                    value={params.lohnProTier}
                    onChange={(e) => handleInputChange('lohnProTier', parseFloat(e.target.value))}
                  />
                </td>
                <td>€</td>
              </tr>
              <tr>
                <td>
                  <button type="button" onClick={calculate}>
                    Berechnen
                  </button>
                </td>
                <td>
                  <button type="button" onClick={handlePrintBilling}>
                    Drucken
                  </button>
                </td>
                <td>
                  <button type="button" onClick={handlePrintMeasurement}>
                    Messprotokoll
                  </button>
                </td>
                <td>
                  <label>
                    <input
                      type="checkbox"
                      checked={showDetails}
                      onChange={(e) => setShowDetails(e.target.checked)}
                    />
                    Details zeigen
                  </label>
                </td>
              </tr>
            </tbody>
          </table>

          {params.kgProPack > 0 && (
            <div className="info-box">
              Es müssen {packagesToOrder} Packungen gekauft werden.
            </div>
          )}

          <h4>Summe der Einnahmen: {totalIncome}</h4>
        </form>

        <h3>Kosten pro Besitzer</h3>
        <table className="results-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Gesamtpreis</th>
            </tr>
          </thead>
          <tbody>
            {ownersCost
              .sort((a, b) => a.name.localeCompare(b.name))
              .map((owner, index) => (
                <tr key={index}>
                  <td>{owner.name}</td>
                  <td className="price-cell">{owner.preis}</td>
                </tr>
              ))}
          </tbody>
        </table>

        {showDetails && (
          <>
            <h3>Details pro Pferd</h3>
            <table className="results-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Besitzer</th>
                  <th>Gewicht (kg)</th>
                  <th>Preis</th>
                </tr>
              </thead>
              <tbody>
                {horsesWithCost
                  .sort((a, b) => a.name.localeCompare(b.name))
                  .map((horse, index) => (
                    <tr key={index}>
                      <td>{horse.name}</td>
                      <td>{horse.owner}</td>
                      <td>{horse.weight}</td>
                      <td className="price-cell">{horse.wkPreis}</td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </>
        )}
      </article>
    </div>
  );
};

export default DewormingCalculator;
